import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEOHead, SEO_CONFIG } from '../components/SEOHead';
import { ImageGalleryModal } from '../components/ui/ImageGalleryModal';
import { 
  Phone, MessageCircle, ArrowRight, Grid3x3, DoorOpen, Frame, AppWindow,
  MoveHorizontal, Building2, Fence, Square, Droplets, DoorClosed,
  GripHorizontal, Building, Layers, Blinds, PanelTop, GlassWater, CheckCircle,
  ArrowUpDown, Images, ZoomIn
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'grid-3x3': Grid3x3,
  'door-open': DoorOpen,
  'frame': Frame,
  'app-window': AppWindow,
  'move-horizontal': MoveHorizontal,
  'building-2': Building2,
  'fence': Fence,
  'stairs': ArrowUpDown,
  'square': Square,
  'droplets': Droplets,
  'door-closed': DoorClosed,
  'grip-horizontal': GripHorizontal,
  'building': Building,
  'layers': Layers,
  'blinds': Blinds,
  'panel-top': PanelTop,
  'glass-water': GlassWater,
};

export const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryModal, setGalleryModal] = useState({ open: false, service: null });
  const location = useLocation();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (location.hash && !loading) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash, loading]);

  const whatsappUrl = `https://wa.me/918512853955?text=${encodeURIComponent('Hi, I am interested in your services.')}`;

  // Open gallery modal with all images
  const openGallery = (service) => {
    const allImages = [service.image, ...(service.images || [])].filter(Boolean);
    setGalleryModal({ open: true, service: { ...service, allImages } });
  };

  const closeGallery = () => {
    setGalleryModal({ open: false, service: null });
  };

  return (
    <div data-testid="services-page">
      <SEOHead {...SEO_CONFIG.services} />
      
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1770992176317-382f51aff35d?w=1920&q=80')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <p className="text-orange-400 font-semibold mb-4">OUR SERVICES</p>
            <h1 className="font-['Oswald'] text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Complete Glass & Aluminium Solutions
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              From interior partitions to exterior facades, we offer comprehensive aluminium and glass 
              solutions for residential and commercial projects in Faridabad and NCR.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="tel:08512853955">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="services-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="services-quote-btn">
                  Get Free Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-sky-600 font-semibold mb-2">WHAT WE OFFER</p>
            <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Service Portfolio
            </h2>
            <div className="section-divider mx-auto"></div>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Each service is delivered with premium materials, expert craftsmanship, and attention to detail.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <div className="h-48 skeleton"></div>
                  <CardContent className="p-6">
                    <div className="h-6 w-3/4 skeleton rounded mb-3"></div>
                    <div className="h-4 skeleton rounded mb-2"></div>
                    <div className="h-4 w-2/3 skeleton rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => {
                const IconComponent = iconMap[service.icon] || Grid3x3;
                const imageCount = 1 + (service.images?.length || 0);
                return (
                  <Card 
                    key={service.id}
                    id={service.id}
                    className="service-card overflow-hidden group"
                    data-testid={`service-card-${service.id}`}
                  >
                    <div 
                      className="service-card-image relative cursor-pointer"
                      onClick={() => openGallery(service)}
                    >
                      <img 
                        src={service.image} 
                        alt={service.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute top-4 left-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-sky-600" />
                      </div>
                      {/* Image count badge */}
                      {imageCount > 1 && (
                        <div className="absolute top-4 right-4">
                          <span className="px-2 py-1 bg-black/60 text-white text-xs font-medium rounded-full flex items-center gap-1">
                            <Images className="w-3 h-3" />
                            {imageCount}
                          </span>
                        </div>
                      )}
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-white/90 rounded-full p-3">
                          <ZoomIn className="w-6 h-6 text-slate-800" />
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 
                        className="font-['Oswald'] font-semibold text-xl text-slate-900 mb-3 group-hover:text-sky-600 transition-colors cursor-pointer"
                        onClick={() => openGallery(service)}
                      >
                        {service.name}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4">{service.description}</p>
                      
                      <div className="space-y-2 mb-6">
                        {service.benefits?.slice(0, 4).map((benefit, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-slate-700">{benefit}</span>
                          </div>
                        ))}
                      </div>

                      <Link to="/contact" state={{ service: service.name }}>
                        <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white">
                          Get Quote
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-50" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-sky-600 font-semibold mb-2">HOW WE WORK</p>
            <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Simple Process
            </h2>
            <div className="section-divider mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Share your requirements and get expert advice' },
              { step: '02', title: 'Measurement', desc: 'We visit your site for accurate measurements' },
              { step: '03', title: 'Quotation', desc: 'Receive detailed quote with no hidden costs' },
              { step: '04', title: 'Installation', desc: 'Professional installation with quality finish' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-sky-500 to-sky-600 rounded-2xl flex items-center justify-center">
                  <span className="font-['Oswald'] text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="font-['Oswald'] font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-sky-600 to-sky-700" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-6">
            Need a Custom Solution?
          </h2>
          <p className="text-sky-100 text-lg mb-8">
            We specialize in custom fabrication. Share your requirements and we'll create the perfect solution.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:08512853955">
              <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50">
                <Phone className="w-5 h-5 mr-2" />
                Call: 085128 53955
              </Button>
            </a>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#25d366] hover:bg-[#20bd5a] text-white">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        isOpen={galleryModal.open}
        onClose={closeGallery}
        images={galleryModal.service?.allImages || []}
        title={galleryModal.service?.name || ''}
      />
    </div>
  );
};

export default Services;
